// Pure, dependency-free math for the trail flyover (no mapbox/turf/DOM imports), so it is unit-testable.

/**
 * Linearly interpolate elevation (metres) at a distance `distKm` along a 3D coordinate array
 * `coords3d` ([lng, lat, elev][]), given the precomputed cumulative distances `cumulativeDistKm`.
 * Clamps to the final elevation at/after the end of the line.
 */
export function getElevationAtDistanceKm(
  distKm: number,
  coords3d: number[][],
  cumulativeDistKm: number[]
): number {
  if (distKm >= cumulativeDistKm[cumulativeDistKm.length - 1]) {
    return coords3d[coords3d.length - 1][2];
  }
  for (let i = 1; i < cumulativeDistKm.length; i++) {
    if (cumulativeDistKm[i] >= distKm) {
      const span = cumulativeDistKm[i] - cumulativeDistKm[i - 1];
      const fraction = span > 0 ? (distKm - cumulativeDistKm[i - 1]) / span : 0;
      return coords3d[i - 1][2] + (coords3d[i][2] - coords3d[i - 1][2]) * fraction;
    }
  }
  return coords3d[coords3d.length - 1][2];
}

/**
 * Frame-rate-independent exponential smoothing factor for a timestep `dtSeconds` and time constant
 * `tauSeconds`. Returns a value in [0, 1]: 0 = keep the previous value, 1 = jump to the target.
 * Using `1 - e^(-dt/tau)` keeps the smoothing consistent regardless of frame rate (a longer frame
 * moves proportionally further toward the target than a short one).
 */
export function exponentialSmoothingAlpha(dtSeconds: number, tauSeconds: number): number {
  if (tauSeconds <= 0) return 1;
  if (dtSeconds <= 0) return 0;
  return 1 - Math.exp(-dtSeconds / tauSeconds);
}

/**
 * Eases a [lng, lat] position toward `target` by `alpha` (0..1). Used to smooth the flyover camera
 * centre so it rounds corners instead of snapping its pan direction at every route vertex; the
 * marker and drawn line still sit on the true head.
 */
export function smoothLngLat(
  prev: [number, number],
  target: [number, number],
  alpha: number
): [number, number] {
  return [
    prev[0] + (target[0] - prev[0]) * alpha,
    prev[1] + (target[1] - prev[1]) * alpha
  ];
}
